import styled from 'styled-components';

import { PADDING_BY_SIZE } from '@wecasa/common/src/components/selectable-card/constants';
import type { WellnessBasketPrices } from '@wecasa/common/src/types/commons';
import colors from '@wecasa/common/src/utils/colors';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPercentage, formatPrice } from '@wecasa/common/src/utils/formatting';

import { translate } from '@wecasa/i18n';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@wecasa/membership/src/constants/country-config';
import type { MembershipOptIn } from '@wecasa/membership/src/types/membership';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';

import SelectablePriceCard, { type SelectablePriceCardProps } from '../selectable-price-card';

const t = translate('modals.membership_paywall.price_cards.member');

const StyledCard = styled(SelectablePriceCard)`
  height: 98px;

  /*
   The full label adds extra height, but it uses absolute position.
   Therefore we need to add extra padding on top so that the rest of the card is still correctly
   horizontally centred.
  */
  padding-top: calc(${PADDING_BY_SIZE.md}px + 13px);
`;

const StyledPriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

type SelectableWellnessMemberPriceCardProps = Pick<SelectablePriceCardProps, 'onPress'> & {
  numberOfSelectedPrestations: number;
  prices: WellnessBasketPrices;
  optInChoice?: MembershipOptIn;
};

const SelectableWellnessMemberPriceCard = ({
  numberOfSelectedPrestations,
  onPress,
  prices,
  optInChoice,
}: SelectableWellnessMemberPriceCardProps) => {
  const {
    totalPrice,
    membership: { reduction: membershipReduction, totalPrice: membershipTotalPrice },
  } = prices;

  const showPrices = numberOfSelectedPrestations > 0;
  const formattedReduction = showPrices
    ? formatPrice(membershipReduction)
    : formatPercentage(MEMBERSHIP_CONFIG_BY_COUNTRY[getCurrentCountry()].wellnessReductionsPercentage);

  return (
    <StyledCard
      isSelected={optInChoice === 'membership'}
      onPress={onPress}
      title={t('.pay_with_club')}
      label={t('.label', { memberReduction: formattedReduction })}
      hasFullWidthLabel
      value='membership'
    >
      {showPrices && (
        <StyledPriceWrapper>
          <Text strikedOut>{formatPrice(totalPrice)}</Text>
          <Text.MDBold color={colors.pink120}>{formatPrice(membershipTotalPrice)}</Text.MDBold>
        </StyledPriceWrapper>
      )}
    </StyledCard>
  );
};

export default SelectableWellnessMemberPriceCard;
